<script setup lang="ts">
/**
 * A frame as flowing text: how a rewritten frame is drawn, and where it is rewritten.
 *
 * Positioned at the frame's box and as wide as it, set in the frame's own
 * size, leading and alignment, so a rewritten paragraph sits where the
 * printed one did and only its line breaks move.
 *
 * Editing is plain `contenteditable` over one span per run. The spans carry
 * their style, so a word typed inside the bold phrase stays bold and the
 * runs can be read back off the DOM when the editing ends: on leaving the
 * frame, or on Escape, which gives up the change.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

import { fontStack, fontVariant, type ImportedFont } from '@/domain/facsimile/fonts'
import { dominantStyle, leading } from '@/domain/facsimile/text'
import type { EditedRun, FacsimileFrame } from '@/domain/facsimile/types'

const props = withDefaults(
  defineProps<{
    frame: FacsimileFrame
    runs: readonly EditedRun[]
    fonts: ReadonlyMap<string, ImportedFont>
    editing?: boolean
  }>(),
  { editing: false },
)

const emit = defineEmits<{
  commit: [runs: EditedRun[]]
  cancel: []
  /** How many lines of text run past the box: 0 when it all fits. */
  overflow: [lines: number]
}>()

/** CSS pixels per point, which is what the box's height is laid out in. */
const PX_PER_PT = 96 / 72

const root = ref<HTMLElement | null>(null)
let cancelled = false

const base = computed(() => dominantStyle(props.frame))

function boxStyle(): Record<string, string> {
  const [left, top, right, bottom] = props.frame.box
  return {
    left: `${left}pt`,
    top: `${top}pt`,
    width: `${right - left}pt`,
    minHeight: `${bottom - top}pt`,
    fontSize: `${base.value.size}pt`,
    lineHeight: String(leading(props.frame)),
    textAlign: props.frame.align,
    color: base.value.colour,
    // Rewritten, a turned frame still reads the way it was printed: up the page, or down it.
    ...(props.frame.rotation ? { transform: `rotate(${props.frame.rotation}deg)` } : {}),
  }
}

function runStyle(run: EditedRun): Record<string, string> {
  const font = props.fonts.get(run.font)
  const variant = fontVariant(font)
  return {
    fontFamily: fontStack(font),
    fontWeight: String(variant.weight),
    fontStyle: variant.style,
    fontSize: `${run.size}pt`,
    color: run.colour,
  }
}

/** The runs back off the DOM: each piece of text takes the style of the span holding it. */
function readRuns(): EditedRun[] {
  const element = root.value
  if (!element) return []
  const runs: EditedRun[] = []
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const span = node.parentElement?.closest<HTMLElement>('[data-font]')
    runs.push({
      text: node.textContent ?? '',
      font: span?.dataset['font'] ?? base.value.font,
      size: Number(span?.dataset['size'] ?? base.value.size),
      colour: span?.dataset['colour'] ?? base.value.colour,
    })
  }
  return runs
}

function finish(): void {
  if (cancelled) return
  emit('commit', readRuns())
}

function onKey(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    cancelled = true
    emit('cancel')
  }
  // Enter would make a new paragraph the frame has no room to know about.
  if (event.key === 'Enter') event.preventDefault()
}

/**
 * How many lines run past the box. Measured on the element, which grows to fit
 * its text: the scale the page is drawn at does not change layout sizes.
 */
function measure(): void {
  const element = root.value
  if (!element || props.editing) return
  const [, top, , bottom] = props.frame.box
  const spare = element.offsetHeight - (bottom - top) * PX_PER_PT
  const line = base.value.size * leading(props.frame) * PX_PER_PT
  emit('overflow', spare > 1 && line > 0 ? Math.ceil(spare / line) : 0)
}

let observer: ResizeObserver | null = null

onMounted(() => {
  if (props.editing || !root.value || typeof ResizeObserver === 'undefined') return
  observer = new ResizeObserver(measure)
  observer.observe(root.value)
})

onBeforeUnmount(() => observer?.disconnect())

onMounted(async () => {
  if (!props.editing) return
  await nextTick()
  root.value?.focus()
  const selection = window.getSelection()
  if (selection && root.value) {
    selection.selectAllChildren(root.value)
    selection.collapseToEnd()
  }
})
</script>

<template>
  <div
    ref="root"
    class="flowing-frame"
    :class="{ 'flowing-frame--editing': editing }"
    :style="boxStyle()"
    :contenteditable="editing ? 'true' : undefined"
    :spellcheck="editing"
    @blur="editing && finish()"
    @keydown="editing && onKey($event)"
  >
    <span
      v-for="(run, index) in runs"
      :key="index"
      :data-font="run.font"
      :data-size="run.size"
      :data-colour="run.colour"
      :style="runStyle(run)"
      v-text="run.text"
    />
  </div>
</template>

<style scoped>
.flowing-frame {
  position: absolute;
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  hyphens: auto;
  font-kerning: normal;
}

.flowing-frame--editing {
  z-index: 3;
  outline: 1.5px solid var(--accent-fill);
  outline-offset: 2px;
  background: rgba(255, 255, 255, 0.92);
  cursor: text;
}
</style>
